/**
 * Employee Profiles API Client
 *
 * Manages reusable employee profiles for quick manual entry.
 */

import { AxiosError } from 'axios';
import {
  EmployeeProfile,
  CreateEmployeeProfileRequest,
  UpdateEmployeeProfileRequest,
} from '@/types/nib/employeeProfile';
import { ApiResponse } from '@/types';
import apiClient, { handleApiResponse } from './client';

export interface ListProfilesParams {
  search?: string;
  category?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  activeOnly?: boolean;
}

export interface ListProfilesResponse {
  profiles: EmployeeProfile[];
  total: number;
  page: number;
  per_page: number;
}

class EmployeeProfilesApiClient {
  private basePath = '/v1/nib';

  /**
   * List employee profiles with server-side search, filters, and pagination.
   * Search uses pg_trgm fuzzy matching on name/NI/nickname (min 2 chars).
   */
  async listProfiles(
    params: ListProfilesParams = {}
  ): Promise<ListProfilesResponse> {
    try {
      const response = await apiClient.get<
        ApiResponse<
          | EmployeeProfile[]
          | { profiles: EmployeeProfile[]; total: number; page: number; per_page: number }
        >
      >(`${this.basePath}/profiles`, {
        params: {
          search: params.search || undefined,
          category: params.category,
          sort_by: params.sortBy,
          sort_order: params.sortOrder,
          page: params.page,
          per_page: params.perPage,
          active_only: params.activeOnly,
        },
      });
      const data = handleApiResponse(response);
      // Handle both legacy array and new paginated response
      if (Array.isArray(data)) {
        return { profiles: data, total: data.length, page: 1, per_page: 50 };
      }
      return {
        profiles: data?.profiles ?? [],
        total: data?.total ?? 0,
        page: data?.page ?? 1,
        per_page: data?.per_page ?? 50,
      };
    } catch (error) {
      if (
        error instanceof AxiosError &&
        [401, 404].includes(error.response?.status ?? 0)
      ) {
        return { profiles: [], total: 0, page: 1, per_page: 50 };
      }
      throw error;
    }
  }

  /**
   * Create a new employee profile.
   */
  async createProfile(
    data: CreateEmployeeProfileRequest
  ): Promise<EmployeeProfile> {
    const response = await apiClient.post<ApiResponse<EmployeeProfile>>(
      `${this.basePath}/profiles`,
      data
    );
    return handleApiResponse(response);
  }

  /**
   * Update an existing employee profile.
   */
  async updateProfile(
    profileId: string,
    data: UpdateEmployeeProfileRequest
  ): Promise<EmployeeProfile> {
    const response = await apiClient.put<ApiResponse<EmployeeProfile>>(
      `${this.basePath}/profiles/${profileId}`,
      data
    );
    return handleApiResponse(response);
  }

  /**
   * Delete an employee profile.
   */
  async deleteProfile(profileId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `${this.basePath}/profiles/${profileId}`
    );
    handleApiResponse(response);
  }

  /**
   * Import employees from a submission as profiles.
   */
  async importFromSubmission(submissionId: string): Promise<number> {
    const response = await apiClient.post<ApiResponse<{ count: number }>>(
      `${this.basePath}/profiles/import`,
      { submission_id: submissionId }
    );
    return handleApiResponse(response).count;
  }
}

export const employeeProfilesApi = new EmployeeProfilesApiClient();
